import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { SettingsProvider } from './context/SettingsContext';

import HomePage       from './pages/HomePage';
import BookingPage    from './pages/BookingPage';
import EstimatePage   from './pages/EstimatePage';
import CarRemovalPage from './pages/CarRemovalPage';
import GalleryPage    from './pages/GalleryPage';
import BlogPage       from './pages/BlogPage';

import LoginPage     from './pages/admin/LoginPage';
import AdminLayout   from './components/AdminLayout';
import Dashboard     from './pages/admin/Dashboard';
import Bookings      from './pages/admin/Bookings';
import Estimates     from './pages/admin/Estimates';
import B2B           from './pages/admin/B2B';
import Invoices      from './pages/admin/Invoices';
import CarPurchases  from './pages/admin/CarPurchases';
import Gallery       from './pages/admin/Gallery';
import Blog          from './pages/admin/Blog';
import Settings      from './pages/admin/Settings';
import api           from './utils/api';
import WhatsAppButton from './components/WhatsAppButton';

// ── Password Reset Page ───────────────────────────────────────────────────
function ResetPasswordPage() {
  const [params]  = useSearchParams();
  const navigate  = useNavigate();
  const token     = params.get('token');
  const [pw, setPw]         = useState('');
  const [pw2, setPw2]       = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [done, setDone]     = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setError('Passwords do not match.'); return; }
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/api/auth/reset-password', { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate('/admin'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed — link may have expired. Request a new one.');
    } finally { setLoading(false); }
  };

  if (!token) return <div className="min-h-screen flex items-center justify-center"><p className="text-red-500 font-bold">Invalid reset link.</p></div>;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-apex-blue-700">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        {done ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><span className="text-3xl">✅</span></div>
            <h2 className="text-xl font-black text-apex-blue-700 mb-2">Password Updated!</h2>
            <p className="text-gray-500 text-sm">Redirecting to login...</p>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-black text-apex-blue-700 mb-1">Set New Password</h2>
            <p className="text-gray-400 text-sm mb-6">Choose a strong password for your admin account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">New Password</label>
                <input type="password" value={pw} onChange={e => setPw(e.target.value)} required placeholder="Min. 8 characters"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Confirm Password</label>
                <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} required placeholder="Repeat password"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500" />
              </div>
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}
              <button type="submit" disabled={loading || !pw || !pw2}
                className="w-full py-3.5 bg-apex-blue-700 text-white rounded-xl font-black disabled:opacity-50">
                {loading ? 'Updating...' : 'Set New Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ── Auth Guard ────────────────────────────────────────────────────────────
function AdminGuard() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    setAuthed(!!localStorage.getItem('apex_token'));
  }, []);

  const handleLogin = () => setAuthed(true);
  const handleLogout = () => {
    localStorage.removeItem('apex_token');
    localStorage.removeItem('apex_admin_name');
    setAuthed(false);
  };

  if (authed === null) return (
    <div className="min-h-screen flex items-center justify-center bg-apex-blue-700">
      <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
    </div>
  );

  if (!authed) return <LoginPage onLogin={handleLogin} />;
  return <AdminLayout onLogout={handleLogout} />;
}

// ── App ────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <SettingsProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/"            element={<HomePage />} />
          <Route path="/book"        element={<BookingPage />} />
          <Route path="/estimate"    element={<EstimatePage />} />
          <Route path="/car-removal" element={<CarRemovalPage />} />
          <Route path="/gallery"     element={<GalleryPage />} />
          <Route path="/blog"        element={<BlogPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/admin" element={<AdminGuard />}>
            <Route index              element={<Dashboard />} />
            <Route path="bookings"    element={<Bookings />} />
            <Route path="estimates"   element={<Estimates />} />
            <Route path="b2b"         element={<B2B />} />
            <Route path="invoices"    element={<Invoices />} />
            <Route path="car-purchases" element={<CarPurchases />} />
            <Route path="gallery"     element={<Gallery />} />
            <Route path="blog"        element={<Blog />} />
            <Route path="settings"    element={<Settings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <WhatsAppButton />
      </BrowserRouter>
    </SettingsProvider>
  );
}